import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const LocationSection = ({ onUpdate, boostInput = false }) => {
  const { fetchCachedList } = useBuild();
  const [currentLocation, setCurrentLocation] = useState('');
  const [boost, setBoost] = useState(1);
  const [locationOptions, setLocationOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch location options on component mount
  useEffect(() => {
    const fetchLocationOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const locationsData = await fetchCachedList('/search/locations');
        setLocationOptions(locationsData);
      } catch (err) {
        console.error('Failed to fetch location options:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLocationOptions();
  }, [fetchCachedList]);

  const handleLocationAction = (actionType) => {
    if (!currentLocation) return;
    
    const updateData = {
      action: actionType,
      filter: 'geo',
      field: 'location',
      value: [currentLocation]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    setCurrentLocation(''); // Reset after adding
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">
          Failed to load locations: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Location</InputLabel>
        <Select
          value={currentLocation}
          label="Location"
          onChange={(e) => setCurrentLocation(e.target.value)}
          disabled={loading}
        >
          {locationOptions.map((location) => (
            <MenuItem key={location.id} value={location.id}>
              {location.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
          disabled={loading}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleLocationAction('include')}
          disabled={!currentLocation || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleLocationAction('boost')}
            disabled={!currentLocation || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleLocationAction('exclude')}
          disabled={!currentLocation || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default LocationSection;

